import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { Section } from "@/components/settings/primitives";
import { api } from "@/lib/api";
import { useUpdateSettings } from "@/hooks/queries";
import { voiceSample } from "@/lib/voice";
import { cn } from "@/lib/utils";

export function VoiceSection({ me }) {
  const update = useUpdateSettings();
  const config = useQuery({ queryKey: ["voice-config"], queryFn: () => api.get("/voice/config"), staleTime: 60_000 });
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const current = me.settings.voice || "warm";

  const preview = (key) => {
    audio.current?.pause();
    if (playing === key) return setPlaying(null);
    audio.current = new Audio(voiceSample(key));
    audio.current.onended = () => setPlaying(null);
    audio.current.play().then(() => setPlaying(key)).catch(() => toast.error("Couldn't play the sample."));
  };

  return (
    <Section title="JARVIS voice" description="The voice JARVIS speaks with. Samples are free to play." testId="settings-voice-section">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup">
        {(config.data?.voices || []).map((v) => (
          <div key={v.key} className={cn("flex items-center gap-3 rounded-xl border px-3.5 py-3 transition-colors", current === v.key ? "border-white/30 bg-white/[0.05]" : "border-white/[0.08] hover:border-white/15")}>
            <button type="button" role="radio" aria-checked={current === v.key} onClick={() => update.mutate({ voice: v.key }, { onError: (e) => toast.error(e.message) })} className="min-w-0 flex-1 text-left" data-testid={`settings-voice-${v.key}`}>
              <span className="block text-sm text-white">{v.label}{v.key === config.data?.default_voice ? " · default" : ""}</span>
              <span className="block text-xs text-zinc-500">{v.description}</span>
            </button>
            <button type="button" onClick={() => preview(v.key)} aria-label={`${playing === v.key ? "Pause" : "Play"} ${v.label} sample`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/12 text-zinc-300 transition-colors hover:text-white" data-testid={`settings-voice-preview-${v.key}`}>
              {playing === v.key ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}
